import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import DownloadIcon from '@/assets/download.svg?react';
import ArcadeIcon from '@/assets/arcade.svg?react';
import PlayIcon from '@/assets/play.svg?react';
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import gameTilePlaceholder from '@/assets/game-tile-placeholder.png';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters, fetchGameById, fetchGames } from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import GameTabs, { TabName } from './GameTabs';
import { useState, useMemo } from 'react';
import GameDetails from './GameDetails';
import GameComplianceDocuments from './GameComplianceDocuments';
import GameMarketingAssets from './GameMarketingAssets';
import { GameSchema, AppFilters } from '@/lib/types';
import GameRtpList from '@/components/GameRtpList/GameRtpList';
import { useNotFoundRedirect } from '@/hooks/useNotFoundRedirect';
import { ga } from '@/lib/analytics';
import { getComplianceCoverageReturnTo } from '@/pages/ComplianceCoverageAdminPage/ComplianceCoverageAdminPageHelpers';
import { parseFiltersFromSearchParams } from '@/pages/Resources/ResourcePageHelpers';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import BackToComplianceCoverage from './BackToComplianceCoverage';

const TAB_NAMES: TabName[] = [
  'gameDetails',
  'complianceDocuments',
  'marketingAssets',
];

const GamePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tabOpen, setTabOpen] = useState<TabName>(() => {
    const tab = searchParams.get('tab') as TabName | null;
    return tab && TAB_NAMES.includes(tab) ? tab : 'gameDetails';
  });
  // Market and File Type from a Compliance Coverage link. They apply to
  // the first visit to the Compliance Documents tab only; any tab click
  // drops them, so later visits start unfiltered, as before.
  const [complianceLinkFilters, setComplianceLinkFilters] = useState<
    Partial<FilterSchema> | undefined
  >(() => {
    const { market, fileTypeCompliance } =
      parseFiltersFromSearchParams(searchParams);
    return {
      ...(market ? { market } : {}),
      ...(fileTypeCompliance ? { fileTypeCompliance } : {}),
    };
  });
  const complianceCoverageReturnTo = getComplianceCoverageReturnTo(
    location.state,
  );
  const params = useParams();
  const studioGameId = params.studioGameId || '';

  const filtersQuery = useQuery<AppFilters>({
    queryKey: ['filters'],
    queryFn: fetchFilters,
    retry: false,
  });

  useNotFoundRedirect({
    queryResult: filtersQuery,
    checkResourceExists: (data) =>
      !!data.games?.find(
        (game) => game.studio_game_id === studioGameId,
      ),
  });

  const gameId = filtersQuery.data?.games?.find(
    (game) => game.studio_game_id === studioGameId,
  )?.game_id;

  const gameQuery = useQuery<GameSchema>({
    queryKey: ['game', gameId],
    queryFn: () => fetchGameById(gameId || 0),
    enabled: !!gameId,
    retry: false,
  });

  useNotFoundRedirect({
    queryResult: gameQuery,
    checkResourceExists: (data) => !!data,
  });
  const { download, loading } = useDownload();
  const {
    id,
    studio_game_id,
    game_name,
    game_group,
    demo_link,
    photo_url,
    rtp,
  } = gameQuery.data || {};
  const watch_trailer_link = false;

  const { data: gamesData, isPending: isGamesDataLoading } = useQuery(
    {
      queryKey: ['games', 'game_group', game_group],
      queryFn: () =>
        fetchGames({
          game_group: game_group ? [game_group] : undefined,
        }),
      enabled: !!gameQuery.data && !!game_group,
    },
  );

  const isLoading =
    gameQuery.isPending ||
    filtersQuery.isPending ||
    isGamesDataLoading;

  const rtpGames = useMemo(() => {
    return gamesData?.games || [];
  }, [gamesData]);

  const hasRtps = useMemo(() => {
    const uniqueRtps = Array.from(
      new Set(
        rtpGames.map((game) => game.rtp).filter((rtp) => rtp != null),
      ),
    );
    return uniqueRtps.length > 0;
  }, [rtpGames]);

  const handleRtpClick = (studioGameId: string, rtp: number) => {
    ga.trackMouseClick({
      component: 'rtp_select',
      game_id: studio_game_id,
      game_name: game_name,
      rtp,
    });
    navigate(`/game/${studioGameId}`);
  };

  const handleDownloadGameSheet = () => {
    if (!id) return;

    download({
      category: 'game_sheets',
      game_ids: [id],
      entity_name: game_name,
      game_name,
    });
  };

  const handleDemoButtonClick = () => {
    if (!demo_link) return;
    ga.trackMouseClick({
      component: 'demo_play_click',
      game_id: studio_game_id,
      game_name: game_name,
    });
    window.open(demo_link, '_blank');
  };

  const handleTabOpen = (tab: TabName) => {
    ga.trackTabView({
      component: 'game_tabs',
      game_id: studio_game_id,
      game_name: game_name,
      tab_name: tab,
    });
    setComplianceLinkFilters(undefined);
    setTabOpen(tab);
  };

  return (
    <Root>
      <div className="page-width-wrapper">
        {complianceCoverageReturnTo ? (
          <BackToComplianceCoverage to={complianceCoverageReturnTo} />
        ) : null}
        <Paper>
          <Properties>
            {isLoading ? (
              <LoaderWrapper
                role="status"
                aria-label="Loading game data"
              >
                <CircularProgress />
              </LoaderWrapper>
            ) : (
              <>
                {photo_url ? (
                  <Hero src={photo_url} aria-label="Game Hero" />
                ) : (
                  <PlaceholderImage
                    src={gameTilePlaceholder}
                    alt="Game tile placeholder"
                    aria-label="No image available"
                  />
                )}
                <Info>
                  <Name>{game_name}</Name>
                  {hasRtps && (
                    <RtpSection>
                      <RtpLabel>
                        <FormattedMessage
                          id="rtpOptions"
                          defaultMessage="RTP options"
                        />
                        {': '}
                      </RtpLabel>
                      <GameRtpList
                        games={rtpGames}
                        onClick={handleRtpClick}
                        currentRtp={rtp}
                      />
                    </RtpSection>
                  )}
                  <Actions>
                    <StyledButton
                      variant="outlined"
                      grow
                      onClick={handleDownloadGameSheet}
                      loading={loading}
                      aria-label="Download Game Sheet Button"
                    >
                      <DownloadIcon />
                      <FormattedMessage
                        id="downloadGameSheet"
                        defaultMessage="Download Game Sheet"
                      />
                    </StyledButton>
                    {watch_trailer_link && (
                      <StyledButton
                        variant="outlined"
                        aria-label="Watch Trailer Button"
                      >
                        <ArcadeIcon />
                      </StyledButton>
                    )}
                    {demo_link && (
                      <StyledButton
                        variant="outlined"
                        onClick={handleDemoButtonClick}
                        aria-label="Play Button"
                      >
                        <PlayIcon />
                      </StyledButton>
                    )}
                  </Actions>
                </Info>
              </>
            )}
          </Properties>
          <Details>
            {isLoading && (
              <DetailsLoaderWrapper
                role="status"
                aria-label="Loading game details"
              >
                <CircularProgress />
              </DetailsLoaderWrapper>
            )}
            {!isLoading && gameId && (
              <>
                <GameTabs open={tabOpen} onOpen={handleTabOpen} />
                {tabOpen === 'gameDetails' && (
                  <GameDetails game={gameQuery.data} />
                )}
                {tabOpen === 'complianceDocuments' && (
                  <GameComplianceDocuments
                    game_id={gameId}
                    game_name={game_name}
                    initialFilters={complianceLinkFilters}
                  />
                )}
                {tabOpen === 'marketingAssets' && (
                  <GameMarketingAssets
                    game_id={gameId}
                    game_group={game_group}
                    game_name={game_name}
                  />
                )}
              </>
            )}
          </Details>
        </Paper>
      </div>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  padding: 40,
  background: theme.palette.grey50,
  // `#root` is a viewport-height flex column, so as a flex item this would
  // otherwise be shrunk to one screen while its content overflowed past it -
  // painting the grey gutters for the first screenful only and leaving them
  // white below, which is what a long asset/document tab shows.
  flexShrink: 0,
  // Was `100%` (of the viewport-height flex container), which ignored the
  // navigation above it and forced a permanent 90px of scroll on short tabs.
  minHeight: `calc(100vh - ${NavigationHeight}px)`,
}));

const Paper = styled('div')(({ theme }) => ({
  borderRadius: theme.radii.xs,
  overflow: 'hidden',
  width: '100%',
  backgroundColor: theme.palette.white,
  display: 'flex',
}));

const Properties = styled('div')(({ theme }) => ({
  backgroundColor: '#131927',
  width: '340px',
  flexShrink: 0,
  // Sized to its own content rather than stretched to match the tab beside
  // it: with a long compliance/marketing list the dark panel used to run the
  // full length of the page.
  alignSelf: 'flex-start',

  [theme.breakpoints.down('xl')]: {
    width: '300px',
  },
}));

const PlaceholderImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '388px',
  objectFit: 'cover',
  display: 'block',

  [theme.breakpoints.down('xl')]: {
    height: '342px',
  },
}));

const Hero = styled('div')<{ src: string }>(({ theme, src }) => ({
  backgroundImage: `url(${src})`,
  backgroundSize: 'cover',
  backgroundPosition: 'top',
  display: 'block',
  width: '100%',
  height: '388px',

  [theme.breakpoints.down('xl')]: {
    height: '342px',
  },
}));

const Info = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.palette.textPrimaryWhite,
  background:
    'linear-gradient(106deg, rgba(19, 25, 39, 0.80) 19.92%, rgba(29, 38, 60, 0.80) 78.48%)',
}));

const Name = styled('div')(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  lineHeight: '28px',
  marginTop: 2,
  marginBottom: theme.spacing(2),
}));

const Actions = styled('div')({
  display: 'flex',
  gap: 12,
  marginBottom: 24,
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'grow',
})<{ grow?: boolean }>(({ theme, grow }) => ({
  border: `1px solid ${theme.palette.white}`,
  color: theme.palette.textPrimaryWhite,
  flexGrow: grow ? 1 : 0,
  padding: '5px 8px',

  path: {
    stroke: theme.palette.white,
  },
}));

const Details = styled('div')({
  width: '100%',
  diplay: 'flex',
  flexDirection: 'column',
  overflowX: 'auto',
});

const RtpSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const RtpLabel = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color: theme.palette.textPrimaryWhite,
  marginBottom: 8,
}));

const LoaderWrapper = styled('div')({
  width: '100%',
  height: '100%',
  minHeight: '500px',
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '100px',
});

const DetailsLoaderWrapper = styled('div')({
  width: '100%',
  height: '100%',
  minHeight: '400px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export default GamePage;
